import { avancementDuChantier } from './costs';
import { echeanceJours } from './dates';
import { litDate } from './periodes';
import { aEncaisser, regleeParAvoir } from './impayes';
import { etatOffre } from './etat-offre';
import { auCentime, compteDansLeCa, htDocument, signeCa, soldeDu, ttcDocument } from './vat';

/**
 * Où en sont les offres et les factures, en ce moment.
 *
 * <p>L'accueil ne montrait que le chiffre facturé : une offre de 5'000 francs
 * acceptée, dont seul l'acompte était parti, n'y laissait voir ni ce qui
 * restait à facturer, ni les offres en attente de réponse, ni les factures
 * oubliées en brouillon. Tout se compte TTC, comme le chiffre d'affaires de
 * l'accueil.
 *
 * @param {object} p pièces de la société
 * @param {object[]} p.devis offres
 * @param {object[]} p.factures factures clients
 * @param {object[]} [p.chantiers] chantiers, pour relier une facture de chantier à son offre
 * @param {Date} [p.maintenant] date de référence, pour les essais
 * @param {boolean} [p.estMag] magasin : une facture sans lien n'est pas prêtée à une offre
 * @returns {object} nombres et montants de chaque étape
 */
export function suiviAffaires({
  devis,
  factures,
  chantiers,
  maintenant = new Date(),
  estMag = false,
}) {
  const offreDuChantier = new Map(
    (chantiers || []).filter((c) => c.devisId != null).map((c) => [c.id, c.devisId]),
  );
  const somme = (liste, montant) => liste.reduce((s, x) => s + montant(x), 0);

  const acceptees = (devis || []).filter((d) => d.statut === 'Accepté');
  // Une facture faite à la main, sans lien avec l'offre, revient à la seule
  // offre acceptée de son client ; s'il en a plusieurs, on ne devine pas.
  const seuleOffre = new Map();
  acceptees.forEach((d) => seuleOffre.set(d.clientId, seuleOffre.has(d.clientId) ? null : d.id));
  // Au magasin, un client régulier est facturé directement, sans offre : sa
  // seule offre acceptée aurait absorbé toutes ses factures.
  // Une offre devenue chantier se facture depuis le chantier, pièces liées :
  // une facture de régie du même client, sans lien, y passait pour la facture
  // finale et fermait la facture d'avancement comme la finale.
  const offresDeChantier = new Set(offreDuChantier.values());
  const devinee = (f) => {
    const o = estMag ? null : seuleOffre.get(f.clientId);
    return o == null || offresDeChantier.has(o) ? null : o;
  };
  const offreDe = (f) => f.devisId ?? offreDuChantier.get(f.siteId) ?? devinee(f) ?? null;
  // Une offre convertie en facture (sans signature en ligne) n'attend plus
  // de réponse : elle restait parmi les offres en attente.
  const facturees = new Set(
    (factures || [])
      .filter(
        (f) =>
          f.devisId != null && f.type !== 'acompte' && f.type !== 'avoir' && f.statut !== 'Annulée',
      )
      .map((f) => String(f.devisId)),
  );
  const enAttente = (devis || []).filter(
    (d) => d.statut === 'En attente' && !facturees.has(String(d.id)),
  );
  const refusees = (devis || []).filter((d) => d.statut === 'Refusé');
  const emises = (factures || []).filter(compteDansLeCa);

  // Ce qui reste à facturer, offre par offre : une offre facturée au-delà de
  // son montant (un avenant) ne réduit pas ce qu'une autre doit encore.
  const factureSur = new Map();
  emises.forEach((f) => {
    const o = offreDe(f);
    if (o != null) factureSur.set(o, (factureSur.get(o) || 0) + signeCa(f) * ttcDocument(f));
  });
  const offresTtc = somme(acceptees, ttcDocument);
  const dejaFacture = somme(acceptees, (d) => Math.min(ttcDocument(d), factureSur.get(d.id) || 0));
  const aFacturer = acceptees.filter((d) => ttcDocument(d) - (factureSur.get(d.id) || 0) > 0.005);

  const brouillons = (factures || []).filter(
    // Un modèle de facture récurrente n'est pas une facture oubliée.
    (f) => f.statut === 'Brouillon' && f.type !== 'avoir' && !f.recurFreq && !f.recur,
  );
  const ouvertes = emises.filter((f) => aEncaisser(f, devis, factures));
  // Jours de retard sur l'échéance (0 si elle n'est pas passée).
  const retard = (f) => {
    const base = litDate(f.date, maintenant);
    if (!base) return 0;
    const ech = new Date(base);
    ech.setDate(ech.getDate() + echeanceJours(f.echeance));
    return Math.max(0, Math.floor((maintenant - ech) / 86400000));
  };
  const enRetard = ouvertes.filter((f) => retard(f) > 0);
  const payees = emises.filter((f) => f.type !== 'avoir' && f.statut === 'Payée');

  // Chantier par chantier : l'acompte, les travaux en plus, la facture
  // finale, et ce qui reste à demander au client.
  const affaires = acceptees.map((d) => {
    const ch = (chantiers || []).find((c) => c.devisId === d.id) || null;
    const taux = +(d.tvaPct ?? 0) || 0;
    // Les avenants passent sous la remise globale de l'offre, comme sur la
    // facture finale du serveur.
    const remise = 1 - (+(d.remisePct ?? 0) || 0) / 100;
    const avenantsHt = (ch?.avenants || []).reduce((t, a) => t + (+a.montant || 0) * remise, 0);
    const avenantsTtc = avenantsHt * (1 + taux / 100);
    const offreTtc = ttcDocument(d);
    const total = offreTtc + avenantsTtc;
    const piece = (f) => ({
      id: f.id,
      num: f.num || f.id,
      ttc: signeCa(f) * ttcDocument(f),
      ht: signeCa(f) * htDocument(f),
      statut: f.statut,
      // Ce que le client a déjà versé : tout, une partie, ou rien.
      percu:
        f.statut === 'Payée'
          ? signeCa(f) * ttcDocument(f)
          : f.statut === 'Partielle'
            ? Math.min(+f.paidAmount || 0, ttcDocument(f))
            : 0,
      payeeLe: f.paidDate || null,
      date: f.date || '',
      acompte: f.type === 'acompte',
      avoir: f.type === 'avoir',
      // Annulée par un avoir : plus rien à encaisser, plus de retard.
      regleeParAvoir: f.type !== 'avoir' && regleeParAvoir(f, factures),
      retard:
        (f.statut === 'Envoyée' || f.statut === 'Partielle') && !regleeParAvoir(f, factures)
          ? retard(f)
          : 0,
    });
    const siennes = emises.filter((f) => offreDe(f) === d.id).map(piece);
    // Une facture préparée mais pas encore envoyée : elle ne compte pas
    // encore, mais la facture finale n'est plus « à faire ».
    const enPreparation = brouillons.filter((f) => offreDe(f) === d.id).map(piece);
    const facture = siennes.reduce((t, f) => t + f.ttc, 0);
    // Le reste se calcule comme la facture finale le fera : hors taxe, puis
    // la TVA arrondie une fois. Additionner des TTC déjà arrondis laissait un
    // centime d'écart avec la facture.
    // Une offre à plusieurs taux (8.1 % et 2.6 %) : le taux de l'offre ne
    // vaut pas pour tout ; le rapport TTC / HT de l'offre, si.
    const plusieursTaux = (d.lignes || []).some(
      (l) => (l.taxe === false ? 0 : l.tva != null ? +l.tva : taux) !== taux,
    );
    const facteur = plusieursTaux && htDocument(d) > 0 ? offreTtc / htDocument(d) : 1 + taux / 100;
    const avecTva = (ht) => auCentime(auCentime(ht) * facteur);
    const totalHt = htDocument(d) + avenantsHt;
    const factureHt = siennes.reduce((t, f) => t + f.ht, 0);
    const prepareHt = enPreparation.reduce((t, f) => t + f.ht, 0);
    const paye = siennes.reduce((t, f) => t + f.percu, 0);
    const avancement = ch ? avancementDuChantier(ch) : 0;
    return {
      id: d.id,
      titre: ch?.nom || d.desc || '',
      clientId: d.clientId,
      chantierId: ch?.id ?? null,
      offreTtc,
      offreNum: d.num || d.id,
      accepteeLe: d.signDate || '',
      avenants: (ch?.avenants || []).map((av) => ({
        id: av.id,
        motif: av.motif || '',
        date: av.date || '',
        ttc: (+av.montant || 0) * remise * (1 + taux / 100),
      })),
      avenantsTtc,
      total,
      // Le même total hors taxe : la TVA de l'offre, avenants compris, est
      // l'écart des deux.
      totalHt,
      acomptes: siennes.filter((f) => f.acompte),
      finales: siennes.filter((f) => !f.acompte && !f.avoir),
      avoirs: siennes.filter((f) => f.avoir),
      enPreparation,
      facture,
      paye,
      reste: Math.max(0, avecTva(totalHt - factureHt)),
      avancement,
      termine: ch?.statut === 'Terminé' || avancement >= 100,
      // Ce que l'avancement permet de demander dès maintenant, au-delà de ce
      // qui est déjà facturé ou préparé. Calculé comme la facture
      // d'avancement du serveur : avant la remise, arrondi, puis remisé —
      // l'écran annonçait 521.13 pour une facture de 521.14.
      avancementAFacturer: (() => {
        const brut = (x) => htDocument({ ...x, remisePct: 0 });
        const avant = (liste) =>
          liste.reduce((t, p) => t + brut(factures.find((f) => f.id === p.id) || {}), 0);
        const base = brut(d) + (ch?.avenants || []).reduce((t, a) => t + (+a.montant || 0), 0);
        const montant =
          auCentime((base * avancement) / 100) - avant(siennes) - avant(enPreparation);
        return montant > 0.005 ? Math.max(0, avecTva(montant * remise)) : 0;
      })(),
    };
  });

  return {
    affaires,
    // « Offres acceptées » : celles qui attendent encore leur facture. Une
    // offre dont la facture finale est partie est comptée dans les factures
    // (envoyées, payées), pas une deuxième fois ici.
    acceptees: (() => {
      const aFaire = acceptees.filter((d) => etatOffre(d, factures, chantiers) === 'Accepté');
      return { n: aFaire.length, ttc: somme(aFaire, ttcDocument) };
    })(),
    dejaFacture,
    resteAFacturer: affaires.reduce((t, a) => t + a.reste, 0),
    offresAFacturer: aFacturer.length,
    enAttente: { n: enAttente.length, ttc: somme(enAttente, ttcDocument) },
    refusees: { n: refusees.length, ttc: somme(refusees, ttcDocument) },
    enRetard: { n: enRetard.length, ttc: somme(enRetard, soldeDu) },
    brouillons: { n: brouillons.length, ttc: somme(brouillons, ttcDocument) },
    nonPayees: { n: ouvertes.length, ttc: somme(ouvertes, soldeDu), enRetard: enRetard.length },
    payees: { n: payees.length, ttc: somme(payees, ttcDocument) },
  };
}

/**
 * Ce que la cloche doit rappeler sur la facturation : un chantier fini sans
 * facture finale, une facture de chantier dont l'échéance est passée.
 *
 * @param {object} p comme `suiviAffaires`
 * @returns {Array<object>} alertes, la plus urgente d'abord
 */
export function alertesFacturation(p) {
  const { affaires } = suiviAffaires(p);
  const alertes = [];
  affaires.forEach((a) => {
    if (
      a.termine &&
      a.finales.length === 0 &&
      !a.enPreparation.some((f) => !f.acompte) &&
      a.reste > 0.005
    ) {
      alertes.push({ genre: 'finale', devisId: a.id, titre: a.titre, montant: a.reste });
    }
    [...a.acomptes, ...a.finales]
      .filter((f) => f.retard > 0)
      .forEach((f) =>
        alertes.push({
          genre: 'retard',
          devisId: a.id,
          factureId: f.id,
          num: f.num,
          titre: a.titre,
          montant: f.ttc,
          jours: f.retard,
        }),
      );
  });
  return alertes.sort((x, y) => (y.jours || 0) - (x.jours || 0));
}

/**
 * L'étape d'une facture dans son affaire : le premier acompte de l'offre,
 * les factures d'avancement qui suivent, la facture finale — ou une facture
 * sans offre.
 *
 * @param {object} f facture
 * @param {object[]} factures toutes les factures, pour situer les acomptes
 * @returns {string} libellé de l'étape, en français (à passer à `tr`)
 */
export function etapeDeLaFacture(f, factures) {
  if (f.type === 'acompte') {
    const premier = (factures || [])
      .filter(
        (x) =>
          x.type === 'acompte' &&
          x.statut !== 'Annulée' &&
          f.devisId != null &&
          String(x.devisId) === String(f.devisId),
      )
      .sort((a, b) => (+a.id || 0) - (+b.id || 0))[0];
    return !premier || premier.id === f.id ? 'Acompte' : 'Facture d’avancement';
  }
  return f.devisId != null ? 'Facture finale' : 'Facture';
}
